(() => {
  'use strict';
  if (window.FlutterHomeV4) return;
  const instances = new Map();
  const concerns = new Map();
  let railCleanup = () => {};
  const sections = (scope = document) => [
    ...(scope.matches?.('[data-home-v4]') ? [scope] : []),
    ...scope.querySelectorAll('[data-home-v4]')
  ];
  const teamKey = element => element.dataset.fhTeam || 'default';
  const getConcern = element => concerns.get(teamKey(element)) || null;

  function enhanceLinks() {
    const profiles = document.querySelector('[data-home-v4="profiles"]');
    document.querySelectorAll('[data-fh-browse]').forEach(link => {
      if (!link.dataset.fhOriginalHref) link.dataset.fhOriginalHref = link.getAttribute('href');
      link.setAttribute('href', profiles ? '#therapists' : link.dataset.fhOriginalHref);
    });
  }

  function updateHero(root) {
    const instance = instances.get(root);
    if (!instance) return;
    const selected = getConcern(root);
    root.querySelectorAll('[data-fh-concern]').forEach(button => {
      button.setAttribute('aria-pressed', String(Boolean(selected && selected.handle === button.dataset.fhConcern)));
    });
    root.querySelector('[data-fh-clear]')?.setAttribute('aria-pressed', String(!selected));
    const label = root.querySelector('[data-fh-result-label]');
    const copy = root.querySelector('[data-fh-result-copy]');
    const linkText = root.querySelector('[data-fh-result-link] span');
    if (selected) {
      if (label) label.textContent = selected.label + ' · A place to explore';
      if (copy) copy.textContent = 'Explore how therapists describe working with ' + selected.label.toLowerCase() + '. Read a profile to get to know their approach.';
      if (linkText) linkText.textContent = 'Explore these profiles';
    } else {
      if (label) label.textContent = instance.initialLabel;
      if (copy) copy.textContent = instance.initialCopy;
      if (linkText) linkText.textContent = instance.initialLink;
    }
  }

  function syncConcern(key) {
    instances.forEach((instance, root) => {
      if (teamKey(root) !== key) return;
      if (root.dataset.homeV4 === 'hero') updateHero(root);
      if (root.dataset.homeV4 === 'profiles') instance.apply?.(true);
    });
  }

  function setupTabs(root, signal) {
    root.querySelectorAll('[data-fh-tabs]').forEach(group => {
      const tabs = [...group.querySelectorAll('[role="tab"]')];
      if (!tabs.length) return;
      const activate = (tab, focus = false) => {
        tabs.forEach(item => {
          const selected = item === tab;
          item.setAttribute('aria-selected', String(selected));
          item.tabIndex = selected ? 0 : -1;
          const panel = document.getElementById(item.getAttribute('aria-controls'));
          if (panel) panel.hidden = !selected;
        });
        if (focus) tab.focus({preventScroll:true});
      };
      tabs.forEach((tab,index) => {
        tab.addEventListener('click', () => activate(tab), {signal});
        tab.addEventListener('keydown', event => {
          let next;
          if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = (index + 1) % tabs.length;
          if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = (index + tabs.length - 1) % tabs.length;
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = tabs.length - 1;
          if (next !== undefined) { event.preventDefault(); activate(tabs[next], true); }
        }, {signal});
      });
      group.addEventListener('fh:show-panel', event => {
        const tab = tabs.find(item => item.getAttribute('aria-controls') === event.detail);
        if (tab) activate(tab);
      }, {signal});
      activate(tabs[0]);
      group.classList.add('is-ready');
    });
  }

  function setupProfiles(root, instance, signal) {
    const cards = [...root.querySelectorAll('[data-fh-card]')];
    const initial = Math.max(1, Number(root.dataset.fhInitial) || 1);
    let shown = initial;
    let matches = cards;
    const more = root.querySelector('[data-fh-more]');
    const status = root.querySelector('[data-fh-filter-status]');
    const empty = root.querySelector('[data-fh-profile-empty]');
    instance.apply = (reset = false) => {
      if (reset) shown = initial;
      const selected = getConcern(root);
      matches = cards.filter(card => !selected || (card.dataset.fhConcerns || '').trim().split(/\s+/).includes(selected.handle));
      const visible = new Set(matches.slice(0,shown));
      cards.forEach(card => { card.hidden = !visible.has(card); });
      if (more) more.hidden = matches.length <= shown;
      if (empty) empty.hidden = matches.length > 0 || cards.length === 0;
      if (status) {
        status.hidden = !selected && shown === initial;
        const count = Math.min(shown,matches.length);
        status.textContent = selected
          ? (matches.length ? 'Showing ' + count + ' of ' + matches.length + ' profiles that list ' + selected.label + '.' : 'No profiles with ' + selected.label + ' are shown here yet.')
          : 'Showing ' + count + ' of ' + matches.length + ' profiles.';
      }
    };
    more?.addEventListener('click', () => {
      const firstNew = matches[shown];
      shown += Math.max(initial,3);
      instance.apply();
      firstNew?.querySelector('h3 a')?.focus();
    }, {signal});
    instance.apply();
  }

  function setupRail() {
    railCleanup();
    railCleanup = () => {};
    const rail = document.querySelector('[data-fh-rail]');
    const anchor = document.querySelector('[data-fh-rail-after]');
    if (!rail) return;
    const setVisible = visible => {
      rail.classList.toggle('is-visible', visible);
      rail.inert = !visible;
      rail.setAttribute('aria-hidden', String(!visible));
    };
    setVisible(false);
    if (!anchor || !('IntersectionObserver' in window)) return;
    const finalActions = document.querySelector('.fh-final-actions');
    const footer = document.querySelector('footer');
    let pastHero = false;
    let finalVisible = false;
    let footerVisible = false;
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.target === anchor) pastHero = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
        if (entry.target === finalActions) finalVisible = entry.isIntersecting;
        if (entry.target === footer) footerVisible = entry.isIntersecting;
      });
      setVisible(pastHero && !finalVisible && !footerVisible);
    }, {threshold:0});
    observer.observe(anchor);
    if (finalActions) observer.observe(finalActions);
    if (footer) observer.observe(footer);
    railCleanup = () => { observer.disconnect(); setVisible(false); };
  }

  function init(scope = document) {
    sections(scope).forEach(root => {
      if (instances.has(root)) return;
      const controller = new AbortController();
      const signal = controller.signal;
      const instance = {destroy:() => controller.abort()};
      instances.set(root,instance);
      if (root.dataset.homeV4 === 'hero') {
        instance.initialLabel = root.querySelector('[data-fh-result-label]')?.textContent || '';
        instance.initialCopy = root.querySelector('[data-fh-result-copy]')?.textContent || '';
        instance.initialLink = root.querySelector('[data-fh-result-link] span')?.textContent || '';
        root.querySelectorAll('[data-fh-concern]').forEach(button => button.addEventListener('click', () => {
          const key = teamKey(root);
          const previous = getConcern(root);
          if (previous?.handle === button.dataset.fhConcern) concerns.delete(key);
          else concerns.set(key, {handle:button.dataset.fhConcern,label:button.dataset.fhLabel});
          syncConcern(key);
        }, {signal}));
        root.querySelector('[data-fh-clear]')?.addEventListener('click', () => {
          const key = teamKey(root); concerns.delete(key); syncConcern(key);
        }, {signal});
        const selected = getConcern(root);
        if (selected && ![...root.querySelectorAll('[data-fh-concern]')].some(button => button.dataset.fhConcern === selected.handle)) {
          concerns.delete(teamKey(root));
          syncConcern(teamKey(root));
        }
        updateHero(root);
      }
      if (root.dataset.homeV4 === 'profiles') setupProfiles(root,instance,signal);
      setupTabs(root,signal);
      root.classList.add('is-ready');
    });
    enhanceLinks();
    setupRail();
  }

  function unload(scope) {
    sections(scope).forEach(root => {
      instances.get(root)?.destroy();
      instances.delete(root);
    });
    railCleanup();
    requestAnimationFrame(() => { enhanceLinks(); setupRail(); });
  }
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => unload(event.target));
  document.addEventListener('shopify:section:reorder', () => { enhanceLinks(); setupRail(); });
  document.addEventListener('shopify:block:select', event => {
    const blockId = event.detail?.blockId;
    if (!blockId) return;
    const panel = [...document.querySelectorAll('[data-fh-block]')].find(item => item.dataset.fhBlock === blockId);
    if (panel?.matches('details')) panel.open = true;
    const group = panel?.closest('[data-fh-tabs]');
    if (group) group.dispatchEvent(new CustomEvent('fh:show-panel', {detail:panel.id}));
  });
  window.FlutterHomeV4 = {init};
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), {once:true});
  else init();
})();
